/* LM Community — shared auth for the storefront.
 *
 * Both the forum page and the community feed need the same three things: turn
 * the Liquid-signed identity blob into a session, offer Google as an
 * alternative, and attach a bearer token to every API call. That logic lives
 * here once so the two pages cannot drift apart on it.
 *
 * Exposes window.LMAuth. Load before lm-forum.js / lm-feed.js.
 */
(function () {
  'use strict';

  var TOKEN_KEY = 'lm_forum_token';

  var config = { api: '', googleClientId: '' };
  var state = { token: null, me: null, googleReady: false, booted: null };
  var listeners = [];

  function notify() {
    listeners.forEach(function (fn) {
      try {
        fn(state.me);
      } catch (e) {
        /* a broken listener must not take down the rest */
      }
    });
  }

  function readToken() {
    try {
      return window.localStorage.getItem(TOKEN_KEY);
    } catch (e) {
      return null;
    }
  }

  function writeToken(token) {
    try {
      if (token) window.localStorage.setItem(TOKEN_KEY, token);
      else window.localStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      /* private browsing — the session just won't persist */
    }
  }

  /**
   * Every API call goes through here. Rejects with an Error carrying `.status`
   * and `.code` so callers can branch on the server's error code rather than
   * matching on message text.
   */
  function api(path, options) {
    options = options || {};
    var headers = options.headers || {};
    if (state.token) headers.Authorization = 'Bearer ' + state.token;

    var init = { method: options.method || 'GET', headers: headers };
    if (options.body !== undefined) {
      if (options.body instanceof FormData) {
        init.body = options.body;
      } else {
        headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(options.body);
      }
    }

    return fetch(config.api + path, init).then(function (response) {
      return response
        .json()
        .catch(function () {
          return {};
        })
        .then(function (payload) {
          if (!response.ok) {
            // A rejected token means the session lapsed. Drop it so the next
            // page load re-authenticates from the Liquid blob instead of
            // looping on 401s.
            if (response.status === 401) {
              state.token = null;
              state.me = null;
              writeToken(null);
              notify();
            }
            var error = new Error(payload.error || 'Something went wrong');
            error.code = payload.code;
            error.status = response.status;
            throw error;
          }
          return payload;
        });
    });
  }

  function storeSession(payload) {
    state.token = payload.token;
    state.me = payload.me;
    writeToken(payload.token);
    notify();
  }

  function readIdentityBlob() {
    var node = document.getElementById('lm-identity');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  /**
   * Resolves once we know who the visitor is (or that they are anonymous).
   * Safe to call from several scripts on one page — the work happens once.
   */
  function boot() {
    if (state.booted) return state.booted;

    state.token = readToken();
    var identity = readIdentityBlob();

    // A customer logged into the store is always re-authenticated from the
    // blob, even when a token is cached. That keeps the store login and the
    // community session from drifting apart if someone switches accounts.
    if (identity) {
      state.booted = api('/api/auth/shopify', { method: 'POST', body: identity })
        .then(function (payload) {
          storeSession(payload);
          return { me: state.me, merged: payload.merged };
        })
        .catch(function (error) {
          state.token = null;
          state.me = null;
          notify();
          return { me: null, error: error };
        });
      return state.booted;
    }

    if (!state.token) {
      state.booted = Promise.resolve({ me: null });
      notify();
      return state.booted;
    }

    state.booted = api('/api/me')
      .then(function (payload) {
        state.me = payload.me;
        if (!payload.me) writeToken(null);
        notify();
        return { me: state.me };
      })
      .catch(function () {
        state.token = null;
        state.me = null;
        writeToken(null);
        notify();
        return { me: null };
      });

    return state.booted;
  }

  function signOut() {
    state.token = null;
    state.me = null;
    writeToken(null);
    notify();
  }

  // --- Google -------------------------------------------------------------

  function handleCredential(credential, onResult) {
    // Signed in already → this is a link. Otherwise a sign-in.
    var request = state.me
      ? api('/api/me/link', { method: 'POST', body: { provider: 'google', credential: credential } })
      : api('/api/auth/google', { method: 'POST', body: { credential: credential } });

    request
      .then(function (payload) {
        if (payload.token) {
          storeSession(payload);
          onResult(null, { merged: payload.merged, linked: false });
        } else {
          state.me = payload.me;
          notify();
          onResult(null, { merged: false, linked: true });
        }
      })
      .catch(function (error) {
        onResult(error, null);
      });
  }

  /**
   * Draws a Google button into `slot`. The GSI script loads async, so this
   * polls briefly for it rather than assuming it has arrived.
   */
  function renderGoogleButton(slot, mode, onResult) {
    if (!config.googleClientId) return;
    var attempts = 0;

    (function waitForGis() {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        if (!state.googleReady) {
          window.google.accounts.id.initialize({
            client_id: config.googleClientId,
            callback: function (response) {
              handleCredential(response.credential, onResult || function () {});
            },
          });
          state.googleReady = true;
        }
        window.google.accounts.id.renderButton(slot, {
          theme: 'outline',
          size: 'medium',
          shape: 'pill',
          text: mode === 'link' ? 'signin_with' : 'continue_with',
        });
        return;
      }
      attempts += 1;
      if (attempts < 40) window.setTimeout(waitForGis, 150);
    })();
  }

  window.LMAuth = {
    /** Call once per page with the values the section rendered into the DOM. */
    init: function (options) {
      config.api = (options.api || '').replace(/\/$/, '');
      config.googleClientId = options.googleClientId || '';
      return window.LMAuth;
    },
    boot: boot,
    api: api,
    me: function () {
      return state.me;
    },
    isSignedIn: function () {
      return Boolean(state.me);
    },
    canPost: function () {
      return Boolean(state.me && state.me.canPost);
    },
    isTeam: function () {
      return Boolean(state.me && state.me.isTeam);
    },
    hasGoogle: function () {
      return Boolean(config.googleClientId);
    },
    signOut: signOut,
    renderGoogleButton: renderGoogleButton,
    /** Fires whenever the signed-in member changes (including to null). */
    onChange: function (fn) {
      listeners.push(fn);
    },
  };
})();
